import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * `React.lazy` that survives a deploy happening under an open tab.
 *
 * ── The failure this fixes ──────────────────────────────────────────────────
 * Vite emits content-hashed chunks. When the frontend is redeployed, the old
 * hashed files are gone: `HtmlSourceEditor-DCv1_Nfg.js` becomes
 * `HtmlSourceEditor-BxmG_wvv.js`. A browser that loaded the app BEFORE the
 * deploy is still running the old module graph, which still points at the old
 * name — so the moment the user opens a lazily-loaded screen, the fetch 404s
 * and React Router shows:
 *
 *   Failed to fetch dynamically imported module: …/HtmlSourceEditor-DCv1_Nfg.js
 *
 * Eagerly-loaded screens keep working, because their code is already in memory.
 * Only the FIRST lazy import after a redeploy fails, which is why a template
 * list could render perfectly while opening the editor died.
 *
 * ── Why a reload is the right response ──────────────────────────────────────
 * The user is running code that no longer exists on the server. There is nothing
 * to retry — refetching the same dead URL fails identically. The only recovery
 * is to fetch the current `index.html` and start again, which is exactly what a
 * reload does. nginx serves `index.html` with `no-cache`, so the reload lands on
 * the new build rather than the cached old one.
 *
 * Deliberately NOT solved by removing `lazy()`: CodeMirror is ~400 KB and has no
 * business in the main bundle for the sake of a fault that lasts one reload.
 *
 * ── Loop safety ─────────────────────────────────────────────────────────────
 * A reload is only attempted once per session-key, recorded in sessionStorage
 * BEFORE reloading. If the chunk is still unfetchable afterwards the cause is
 * not staleness — the network is down, or the deploy is genuinely broken — so
 * the error is rethrown and surfaces normally instead of reloading forever.
 * sessionStorage (not localStorage) so a genuinely new session can try again.
 */
// The signature mirrors React's own `lazy` exactly, including the `any` in the
// constraint. Anything narrower — `ComponentType<unknown>`, or parameterising on
// the props — makes TypeScript collapse the component type to `never` at the
// call site and silently erases every prop. This is the one place the rule is
// worth suppressing, and only here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  /** Stable identifier for this import site — used as the retry marker. */
  chunkName: string,
): LazyExoticComponent<T> {
  return lazy<T>(async (): Promise<{ default: T }> => {
    const marker = `chunk-reload:${chunkName}`;

    try {
      const mod = await factory();
      // Loaded cleanly — clear any marker so a future stale deploy can recover.
      safeRemove(marker);
      return mod;
    } catch (error) {
      if (safeGet(marker) === null && isStaleChunkError(error)) {
        safeSet(marker, String(Date.now()));
        window.location.reload();
        // Never resolves: the page is being torn down. Returning here would let
        // React render a broken boundary in the frame before the reload lands.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

/**
 * Whether the failure looks like a missing chunk rather than a bug in the module.
 *
 * A module that throws while evaluating must NOT trigger a reload — that would
 * hide a real error behind an infinite-looking refresh. Browsers word this
 * differently, hence the several patterns.
 */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /'text\/html' is not a valid javascript mime type/i.test(message)
  );
}

// sessionStorage throws in some privacy modes; recovery must not depend on it.
function safeGet(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the reload still happens, just unguarded once */
  }
}

function safeRemove(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}
